import React, { useState } from 'react';
import partial from 'lodash/partial';

import {
  resendSignupCode,
  confirmSignup,
  sendEmailVerificationCode,
  confirmEmail,
} from 'components/authorization/cognitoUtils';

import {
  SignIn,
  ResetPassword,
  VerificationCode,
  ForgotPasswordSendMeCode,
  ForgotPasswordSubmitCode,
} from '.';

export enum AuthDisplayState {
  Disabled,
  SignIn,
  PhoneVerificationCode,
  EmailVerificationCode,
  ForgotPasswordSendMeCode,
  ForgotPasswordSubmitCode,
  ResetPassword,
}

interface Props {
  show: AuthDisplayState;
  displayStateEvent: ContentCommunicator;
}

export type ContentToggler = React.Dispatch<void>;
export type ContentCommunicator = React.Dispatch<AuthDisplayState>;

export const AuthContainer: React.FC<Props> = ({ show, displayStateEvent }) => {
  const signin = partial(displayStateEvent, AuthDisplayState.SignIn);
  const disabled = partial(displayStateEvent, AuthDisplayState.Disabled);
  const sendPasswordCode = partial(
    displayStateEvent,
    AuthDisplayState.ForgotPasswordSendMeCode,
  );
  const phoneCode = partial(
    displayStateEvent,
    AuthDisplayState.PhoneVerificationCode,
  );
  const submitPasswordCode = partial(
    displayStateEvent,
    AuthDisplayState.ForgotPasswordSubmitCode,
  );

  // used only by <ForgotPasswordSendMeCode/> and <ForgotPasswordSubmitCode/>
  // to keep track of a username aquired by the former but required by the latter.
  const [username, setUsername] = useState('');

  return (
    <>
      {show === AuthDisplayState.SignIn && (
        <SignIn
          switchToClose={disabled}
          switchToForgotPassword={sendPasswordCode}
          switchToVerificationCode={phoneCode}
        />
      )}
      {show === AuthDisplayState.PhoneVerificationCode && (
        <VerificationCode
          handleSuccess={signin}
          switchToClose={disabled}
          attribute="phone"
          resender={resendSignupCode}
          submitter={confirmSignup}
        />
      )}
      {show === AuthDisplayState.EmailVerificationCode && (
        <VerificationCode
          handleSuccess={disabled}
          switchToClose={disabled}
          attribute="email"
          resender={sendEmailVerificationCode}
          submitter={confirmEmail}
        />
      )}
      {show === AuthDisplayState.ForgotPasswordSendMeCode && (
        <ForgotPasswordSendMeCode
          switchToClose={submitPasswordCode}
          switchToSignIn={signin}
          setUsername={setUsername}
        />
      )}
      {show === AuthDisplayState.ForgotPasswordSubmitCode && (
        <ForgotPasswordSubmitCode
          switchToClose={disabled}
          switchToSendCode={sendPasswordCode}
          username={username}
        />
      )}
      {show === AuthDisplayState.ResetPassword && (
        <ResetPassword switchToClose={disabled} />
      )}
    </>
  );
};
